import React, { useState, useEffect } from 'react';
import { Phone, Clock, Users, TrendingUp, Star, CheckCircle, ArrowRight, Menu, X, MessageSquare, Calendar, ShoppingCart, Building2, MapPin, Briefcase, DollarSign } from 'lucide-react';

const AIPhoneAgentApp = () => {
  const [activeForm, setActiveForm] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Form states
  const [marketingForm, setMarketingForm] = useState({
    name: '', phone: '', country: '', howCanHelp: ''
  });
  const [businessForm, setBusinessForm] = useState({
    name: '', phone: '', businessType: '', businessTypeOther: '', 
    interestedInTrial: false, businessCountry: '', additionalNotes: ''
  });
  const [investorForm, setInvestorForm] = useState({
    name: '', phone: '', howCanDevelop: '', investmentType: '', experience: ''
  });

  // API call function
  const submitForm = async (endpoint, data) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`https://sponser-028383c774c1.herokuapp.com/api/${endpoint}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus({ type: 'success', message: result.message });
        // Reset form
        if (endpoint === 'marketing') setMarketingForm({ name: '', phone: '', country: '', howCanHelp: '' });
        else if (endpoint === 'business') setBusinessForm({ name: '', phone: '', businessType: '', businessTypeOther: '', interestedInTrial: false, businessCountry: '', additionalNotes: '' });
        else setInvestorForm({ name: '', phone: '', howCanDevelop: '', investmentType: '', experience: '' });
        setTimeout(() => setActiveForm(null), 2000);
      } else {
        setSubmitStatus({ type: 'error', message: result.message || 'حدث خطأ، حاول مرة أخرى' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'خطأ في الاتصال، تأكد من اتصالك بالإنترنت' });
    }
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  const handleMarketingSubmit = (e) => {
    e.preventDefault();
    submitForm('marketing', marketingForm);
  };

  const handleBusinessSubmit = (e) => {
    e.preventDefault();
    submitForm('business', businessForm);
  };

  const handleInvestorSubmit = (e) => {
    e.preventDefault();
    submitForm('investor', investorForm);
  };

  // Counter Animation
  const AnimatedCounter = ({ end }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      const duration = 2000;
      const increment = end / (duration / 50);
      const timer = setInterval(() => {
        setCount(prev => {
          if (prev < end) {
            return Math.min(prev + increment, end);
          }
          clearInterval(timer);
          return end;
        });
      }, 50);
      return () => clearInterval(timer);
    }, [end]);
    
    return <span>{Math.floor(count)}</span>;
  };

  return (
    <>
      {/* Bootstrap CSS */}
      <link 
        href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.2/css/bootstrap.min.css" 
        rel="stylesheet" 
      />
      
      {/* Custom CSS */}
      <style jsx>{`
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: linear-gradient(135deg, #f0f9f0 0%, #fff5e6 100%);
          direction: rtl;
        }
        
        :root {
          --primary-green: #576b2f;
          --secondary-brown: #8b4513;
          --light-green: #f0f9f0;
          --light-brown: #fff5e6;
        }
        
        .bg-primary-green {
          background-color: var(--primary-green) !important;
        }
        
        .bg-secondary-brown {
          background-color: var(--secondary-brown) !important;
        }
        
        .text-primary-green {
          color: var(--primary-green) !important;
        }
        
        .text-secondary-brown {
          color: var(--secondary-brown) !important;
        }
        
        .btn-primary-green {
          background-color: var(--primary-green);
          border-color: var(--primary-green);
          color: white;
        }
        
        .btn-primary-green:hover {
          background-color: #4a5c26;
          border-color: #4a5c26;
          color: white;
        }
        
        .btn-secondary-brown {
          background-color: var(--secondary-brown);
          border-color: var(--secondary-brown);
          color: white;
        }
        
        .btn-secondary-brown:hover {
          background-color: #7a3d11;
          border-color: #7a3d11;
          color: white;
        }
        
        .btn-gradient {
          background: linear-gradient(45deg, var(--primary-green), var(--secondary-brown));
          border: none;
          color: white;
        }
        
        .btn-gradient:hover {
          opacity: 0.9;
          color: white;
        }
        
        .hero-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(45deg, var(--primary-green), var(--secondary-brown));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 30px;
        }
        
        .feature-card {
          background: white;
          border-radius: 15px;
          padding: 30px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          height: 100%;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }
        
        .stats-card {
          background: white;
          border-radius: 10px;
          padding: 20px;
          text-align: center;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          margin-bottom: 15px;
        }
        
        .service-card {
          background: linear-gradient(135deg, var(--light-green), var(--light-brown));
          border-radius: 15px;
          padding: 30px;
          text-align: center;
          height: 100%;
          transition: all 0.3s ease;
        }
        
        .service-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(0,0,0,0.15);
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
          padding: 20px;
        }
        
        .modal-content-custom {
          background: white;
          border-radius: 15px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .alert-floating {
          position: fixed;
          top: 20px;
          left: 20px;
          right: 20px;
          z-index: 1100;
          margin: 0;
        }
        
        .navbar-custom {
          background: white !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        
        .footer-custom {
          background: #2c3e50;
          color: white;
          padding: 50px 0 20px;
        }
        
        .animate-counter {
          font-size: 2rem;
          font-weight: bold;
        }
        
        @media (max-width: 768px) {
          .hero-icon {
            width: 80px;
            height: 80px;
          }
          
          .animate-counter {
            font-size: 1.5rem;
          }
        }
      `}</style>

      <div>
        {/* Header */}
        <nav className="navbar navbar-expand-lg navbar-custom sticky-top">
          <div className="container">
            <div className="navbar-brand d-flex align-items-center">
              <Phone className="me-2 text-primary-green" size={32} />
              <span className="fw-bold text-primary-green fs-4">مساعد الهاتف الذكي</span>
            </div>
            
            <button 
              className="navbar-toggler" 
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`}>
              <ul className="navbar-nav ms-auto">
                <li className="nav-item">
                  <a className="nav-link" href="#about">حول الخدمة</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#features">المميزات</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#join">انضم إلينا</a>
                </li>
              </ul>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-5">
          <div className="container">
            <div className="row justify-content-center text-center">
              <div className="col-lg-10">
                <div className="hero-icon">
                  <Phone size={50} color="white" />
                </div>
                
                <h1 className="display-4 fw-bold mb-4">
                  مساعد الهاتف الذكي
                  <br />
                  <span className="text-primary-green">بالذكاء الاصطناعي</span>
                </h1>
                
                <p className="lead mb-5">
                  ثورة في عالم خدمة العملاء! مساعد ذكي يرد على مكالماتك، يحجز المواعيد، ويأخذ الطلبات
                  <br />
                  <strong className="text-primary-green">24 ساعة، 7 أيام في الأسبوع</strong>
                </p>
              </div>
            </div>

            {/* Features Grid */}
            <div className="row g-4 mb-5">
              <div className="col-md-4">
                <div className="feature-card text-center">
                  <Clock className="text-primary-green mb-3" size={48} />
                  <h4 className="fw-bold mb-3">متاح دائماً</h4>
                  <p className="text-muted">يعمل 24/7 بدون توقف أو إجازات</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="feature-card text-center">
                  <TrendingUp className="text-secondary-brown mb-3" size={48} />
                  <h4 className="fw-bold mb-3">توفير المال</h4>
                  <p className="text-muted">لا حاجة لموظف استقبال بدوام كامل</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="feature-card text-center">
                  <Star className="text-primary-green mb-3" size={48} />
                  <h4 className="fw-bold mb-3">خدمة مثالية</h4>
                  <p className="text-muted">لا ينسى، لا يتعب، دائماً مهذب</p>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="row g-3">
              <div className="col-6 col-md-3">
                <div className="stats-card">
                  <div className="animate-counter text-primary-green">
                    <AnimatedCounter end={99} />%
                  </div>
                  <small className="text-muted">دقة في الرد</small>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="stats-card">
                  <div className="animate-counter text-secondary-brown">
                    <AnimatedCounter end={85} />%
                  </div>
                  <small className="text-muted">توفير في التكاليف</small>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="stats-card">
                  <div className="animate-counter text-primary-green">
                    <AnimatedCounter end={24} />/<AnimatedCounter end={7} />
                  </div>
                  <small className="text-muted">ساعات العمل</small>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="stats-card">
                  <div className="animate-counter text-secondary-brown">
                    <AnimatedCounter end={0} />
                  </div>
                  <small className="text-muted">أخطاء بشرية</small>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-5 bg-white">
          <div className="container">
            <h2 className="display-5 fw-bold text-center mb-5">
              كيف يعمل <span className="text-primary-green">مساعد الهاتف الذكي</span>؟
            </h2>
            
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="row g-4">
                  <div className="col-12">
                    <div className="d-flex">
                      <div className="bg-primary-green rounded-5 p-3 m-2 ">
                        <Phone color="white" size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">يرد على المكالمات</h5>
                        <p className="text-muted">المساعد الذكي يجيب على كل مكالمة بشكل فوري ومهذب</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <div className="d-flex">
                      <div className="bg-secondary-brown rounded-5 p-3 m-2">
                        <MessageSquare color="white" size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">يفهم الطلبات</h5>
                        <p className="text-muted">بفضل الذكاء الاصطناعي، يفهم ما يريده العميل بدقة</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <div className="d-flex">
                      <div className="bg-primary-green rounded-5 p-3 m-2">
                        <Calendar color="white" size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">ينظم المواعيد</h5>
                        <p className="text-muted">يحجز المواعيد، يؤكدها، يرسل تذكيرات</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <div className="d-flex">
                      <div className="bg-secondary-brown rounded-5 p-3 m-2">
                        <CheckCircle color="white" size={24} />
                      </div>
                      <div>
                        <h5 className="fw-bold">يسجل البيانات</h5>
                        <p className="text-muted">كل معلومة مهمة يتم حفظها وتنظيمها</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-6 text-center">
                <div className="service-card">
                  <div className="hero-icon mx-auto mb-4">
                    <Phone size={48} color="white" style={{animation: 'pulse 2s infinite'}} />
                  </div>
                  <h4 className="fw-bold mb-4">مناسب لجميع الأعمال</h4>
                  <div className="row g-3 text-sm">
                    <div className="col-6">
                      <ShoppingCart className="text-primary-green me-2" size={20} />
                      المطاعم
                    </div>
                    <div className="col-6">
                      <Building2 className="text-secondary-brown me-2" size={20} />
                      المستشفيات
                    </div>
                    <div className="col-6">
                      <Users className="text-primary-green me-2" size={20} />
                      الصالونات
                    </div>
                    <div className="col-6">
                      <MapPin className="text-secondary-brown me-2" size={20} />
                      الفنادق
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-5" style={{background: 'linear-gradient(135deg, #f0f9f0 0%, #fff5e6 100%)'}}>
          <div className="container">
            <h2 className="display-5 fw-bold text-center mb-5">
              لماذا <span className="text-primary-green">مساعد الهاتف الذكي</span>؟
            </h2>

            <div className="row g-4 mb-5">
              {[
                { icon: Clock, title: "متاح 24/7", desc: "لا إجازات، لا مرض، لا تأخير - خدمة مستمرة", color: "primary-green" },
                { icon: DollarSign, title: "توفير المال", desc: "تكلفة أقل بكثير من موظف بدوام كامل", color: "secondary-brown" },
                { icon: Star, title: "خدمة مثالية", desc: "لا يتعب، لا ينسى، دائماً مهذب ومنظم", color: "primary-green" },
                { icon: TrendingUp, title: "زيادة المبيعات", desc: "لا تفوت أي مكالمة أو فرصة بيع", color: "secondary-brown" },
                { icon: Users, title: "رضا العملاء", desc: "خدمة سريعة ودقيقة في كل مكالمة", color: "primary-green" },
                { icon: CheckCircle, title: "سهل الاستخدام", desc: "تثبيت بسيط وإدارة سهلة", color: "secondary-brown" }
              ].map((feature, index) => (
                <div key={index} className="col-lg-4 col-md-6">
                  <div className="feature-card">
                    <feature.icon className={`text-${feature.color} mb-3`} size={48} />
                    <h5 className="fw-bold mb-3">{feature.title}</h5>
                    <p className="text-muted">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Cost Comparison */}
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="feature-card">
                  <h3 className="fw-bold text-center mb-4">احسب التوفير</h3>
                  <div className="row g-4">
                    <div className="col-md-6 text-center">
                      <h5 className="text-danger mb-2">الحل التقليدي</h5>
                      <p className="text-muted small">موظف استقبال بدوام كامل</p>
                      <div className="display-6 fw-bold text-danger">400$ شهرياً</div>
                    </div>
                    <div className="col-md-6 text-center">
                      <h5 className="text-primary-green mb-2">مساعد الهاتف الذكي</h5>
                      <p className="text-muted small">خدمة 24/7 بدون توقف</p>
                      <div className="display-6 fw-bold text-primary-green">اوفر بما يقارب 50%</div>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Join Us Section */}
        <section id="join" className="py-5 bg-white">
          <div className="container">
            <h2 className="display-5 fw-bold text-center mb-2">
              انضم إلى <span className="text-primary-green">مستقبل خدمة العملاء</span>
            </h2>
            <p className="lead text-center text-muted mb-5">
              سواء كنت صاحب عمل، مسوق، أو مستثمر - هناك مكان لك معنا!
            </p>

            <div className="row g-4">
              <div className="col-lg-4">
                <div className="service-card">
                  <Briefcase className="text-primary-green mb-4" size={48} />
                  <h4 className="fw-bold mb-3">أصحاب الأعمال</h4>
                  <p className="text-muted mb-4">جرب الخدمة على عملك واكتشف الفرق</p>
                  <button 
                    className="btn btn-gradient color-white w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => setActiveForm('business')}
                  >
                    جرب الآن
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="service-card">
                  <Users className="text-secondary-brown mb-4" size={48} />
                  <h4 className="fw-bold mb-3">شركاء التسويق</h4>
                  <p className="text-muted mb-4">ساعدنا في نشر هذه التقنية الثورية</p>
                  <button 
                    className="btn btn-gradient color-white w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => setActiveForm('marketing')}
                  >
                    انضم كمسوق
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="service-card">
                  <TrendingUp className="text-primary-green mb-4" size={48} />
                  <h4 className="fw-bold mb-3">المستثمرون</h4>
                  <p className="text-muted mb-4">استثمر في مستقبل الذكاء الاصطناعي</p>
                  <button 
                    className="btn btn-gradient w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => setActiveForm('investor')}
                  >
                    استثمر معنا
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer-custom">
          <div className="container">
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="d-flex align-items-center mb-3">
                  <Phone className="me-2" size={32} />
                  <h4 className="fw-bold mb-0">مساعد الهاتف الذكي</h4>
                </div>
                <p className="text-light">
                  نحن نعمل على تطوير مستقبل خدمة العملاء باستخدام أحدث تقنيات الذكاء الاصطناعي.
                </p>
              </div>
              
              <div className="col-lg-4">
                <h5 className="fw-semibold mb-3">مناسب للأعمال</h5>
                <ul className="list-unstyled">
                  <li className="mb-2 d-flex align-items-center">
                    <ShoppingCart className="me-2" size={16} />
                    المطاعم والكافيهات
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Building2 className="me-2" size={16} />
                    المستشفيات والعيادات
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Users className="me-2" size={16} />
                    صالونات التجميل
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <MapPin className="me-2" size={16} />
                    الفنادق والمنتجعات
                  </li>
                </ul>
              </div>
              
              <div className="col-lg-4">
                <h5 className="fw-semibold mb-3">لماذا نحن؟</h5>
                <ul className="list-unstyled">
                  {['تقنية متطورة', 'دعم فني متميز', 'أسعار تنافسية', 'نتائج مضمونة'].map((item, index) => (
                    <li key={index} className="mb-2 d-flex align-items-center">
                      <CheckCircle className="me-2" size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <hr className="my-4" />
            <div className="text-center">
              <p className="mb-1">© 2024 مساعد الهاتف الذكي - جميع الحقوق محفوظة</p>
              <small className="text-muted">تم التطوير بـ ❤️ لخدمة الأعمال العربية</small>
            </div>
          </div>
        </footer>

        {/* Status Alert */}
        {submitStatus && (
          <div className={`alert alert-floating ${submitStatus.type === 'success' ? 'alert-success' : 'alert-danger'} alert-dismissible`}>
            <div className="d-flex align-items-center">
              {submitStatus.type === 'success' ? 
                <CheckCircle className="me-2" size={20} /> : 
                <X className="me-2" size={20} />
              }
              {submitStatus.message}
            </div>
          </div>
        )}

        {/* Modal Forms */}
        {activeForm && (
          <div className="modal-overlay">
            <div className="modal-content-custom">
              <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="fw-bold mb-0">
                    {activeForm === 'marketing' && 'انضم كشريك تسويق'}
                    {activeForm === 'business' && 'جرب الخدمة على عملك'}
                    {activeForm === 'investor' && 'استثمر في المستقبل'}
                  </h4>
                  <button 
                    className="btn-close"
                    onClick={() => setActiveForm(null)}
                    disabled={isSubmitting}
                  ></button>
                </div>

                {/* Marketing Form */}
                {activeForm === 'marketing' && (
                  <form onSubmit={handleMarketingSubmit}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">الاسم</label>
                      <input
                        type="text"
                        className="form-control"
                        value={marketingForm.name}
                        onChange={(e) => setMarketingForm({...marketingForm, name: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">رقم الهاتف</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={marketingForm.phone}
                        onChange={(e) => setMarketingForm({...marketingForm, phone: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">الدولة</label>
                      <input
                        type="text"
                        className="form-control"
                        value={marketingForm.country}
                        onChange={(e) => setMarketingForm({...marketingForm, country: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label fw-semibold">كيف يمكنك مساعدتنا؟</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={marketingForm.howCanHelp}
                        onChange={(e) => setMarketingForm({...marketingForm, howCanHelp: e.target.value})}
                        placeholder="اشرح خبرتك في التسويق وكيف يمكنك مساعدتنا..."
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <button 
                      type="submit" 
                      className="btn btn-secondary-brown w-100"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                    </button>
                  </form>
                )}

                {/* Business Form */}
                {activeForm === 'business' && (
                  <form onSubmit={handleBusinessSubmit}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">الاسم</label>
                      <input
                        type="text"
                        className="form-control"
                        value={businessForm.name}
                        onChange={(e) => setBusinessForm({...businessForm, name: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">رقم الهاتف</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={businessForm.phone}
                        onChange={(e) => setBusinessForm({...businessForm, phone: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">نوع العمل</label>
                      <select
                        className="form-select"
                        value={businessForm.businessType}
                        onChange={(e) => setBusinessForm({...businessForm, businessType: e.target.value})}
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">اختر نوع العمل</option>
                        <option value="restaurant">مطعم</option>
                        <option value="hospital">مستشفى</option>
                        <option value="clinic">عيادة</option>
                        <option value="hotel">فندق</option>
                        <option value="retail">متجر</option>
                        <option value="salon">صالون تجميل</option>
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    {businessForm.businessType === 'other' && (
                      <div className="mb-3">
                        <label className="form-label fw-semibold">نوع العمل الآخر</label>
                        <input
                          type="text"
                          className="form-control"
                          value={businessForm.businessTypeOther}
                          onChange={(e) => setBusinessForm({...businessForm, businessTypeOther: e.target.value})}
                          placeholder="حدد نوع عملك..."
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">الدولة</label>
                      <input
                        type="text"
                        className="form-control"
                        value={businessForm.businessCountry}
                        onChange={(e) => setBusinessForm({...businessForm, businessCountry: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="interestedInTrial"
                          checked={businessForm.interestedInTrial}
                          onChange={(e) => setBusinessForm({...businessForm, interestedInTrial: e.target.checked})}
                          disabled={isSubmitting}
                        />
                        <label className="form-check-label" htmlFor="interestedInTrial">
                          أريد تجربة الخدمة على عملي
                        </label>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="form-label fw-semibold">ملاحظات إضافية (اختياري)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={businessForm.additionalNotes}
                        onChange={(e) => setBusinessForm({...businessForm, additionalNotes: e.target.value})}
                        placeholder="أي ملاحظات أو أفكار إضافية..."
                        disabled={isSubmitting}
                      />
                    </div>
                    <button 
                      type="submit" 
                      className="btn btn-primary-green w-100"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                    </button>
                  </form>
                )}

                {/* Investor Form */}
                {activeForm === 'investor' && (
                  <form onSubmit={handleInvestorSubmit}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">الاسم</label>
                      <input
                        type="text"
                        className="form-control"
                        value={investorForm.name}
                        onChange={(e) => setInvestorForm({...investorForm, name: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">رقم الهاتف</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={investorForm.phone}
                        onChange={(e) => setInvestorForm({...investorForm, phone: e.target.value})}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">نوع الاستثمار</label>
                      <select
                        className="form-select"
                        value={investorForm.investmentType}
                        onChange={(e) => setInvestorForm({...investorForm, investmentType: e.target.value})}
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">اختر نوع الاستثمار</option>
                        <option value="financial">استثمار مالي</option>
                        <option value="technical">تطوير تقني</option>
                        <option value="strategic">شراكة استراتيجية</option>
                        <option value="advisory">استشارات</option>
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">كيف يمكنك تطوير الفكرة؟</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={investorForm.howCanDevelop}
                        onChange={(e) => setInvestorForm({...investorForm, howCanDevelop: e.target.value})}
                        placeholder="اشرح كيف يمكنك المساهمة في تطوير المشروع..."
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label fw-semibold">الخبرة السابقة (اختياري)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={investorForm.experience}
                        onChange={(e) => setInvestorForm({...investorForm, experience: e.target.value})}
                        placeholder="خبرتك في الاستثمار أو التطوير..."
                        disabled={isSubmitting}
                      />
                    </div>
                    <button 
                      type="submit" 
                      className="btn btn-gradient w-100"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bootstrap JS */}
      <script src="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.2/js/bootstrap.bundle.min.js"></script>
    </>
  );
};

export default AIPhoneAgentApp;